import {useEffect, useMemo, useState} from "react";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import {participationBeforeEvent} from "@/utils/matchEvents";
import "./handleEvent.scss";

const SCORING_TYPES = [
	{value: "goal", label: "Gol su azione"},
	{value: "own_goal", label: "Autogol"},
	{value: "penalty_scored", label: "Rigore segnato"},
	{value: "penalty_missed", label: "Rigore sbagliato"},
];
const SANCTION_TYPES = [
	{value: "yellow_card", label: "Cartellino giallo"},
	{value: "double_yellow_card", label: "Doppio cartellino giallo"},
	{value: "red_card", label: "Cartellino rosso"},
];

function HandleEventModal({open, confirm, close, initialData}) {
	const [formData, setFormData] = useState({});
	const [error, setError] = useState("");
	const players = initialData?.players ?? [];
	const options = useMemo(
		() =>
			(initialData?.players ?? []).map((p) => ({
				value: p.playerId ?? p.id,
				playerId: p.playerId ?? null,
				participationId: p.id,
				provisional: p.provisional,
				label: `${p.playerNumber ?? "–"} – ${p.playerName || `${p.player?.firstName ?? ""} ${p.player?.lastName ?? "Nome non indicato"}`}`,
				playerNumber: p.playerNumber,
			})),
		[initialData?.players],
	);
	useEffect(() => {
		if (!open) return;
		const e = initialData?.event;
		const option = (id) => options.find((p) => p.value === id) ?? null;
		setFormData({
			goalType:
				SCORING_TYPES.find((t) => t.value === e?.eventType) ?? SCORING_TYPES[0],
			player: option(e?.playerId ?? e?.scorerParticipationId),
			opponentNumber: "",
			opponentName: "",
			substitutePlayer: option(
				e?.substitutePlayerId ?? e?.outgoingParticipationId,
			),
			opponentOutgoingNumber: "",
			opponentOutgoingName: "",
			substitutionReason: e?.substitutionReason ?? "normal",
			assistPlayer: option(e?.assistPlayerId ?? e?.assistParticipationId),
			opponentAssistNumber: "",
			opponentAssistName: "",
			eventType: SANCTION_TYPES.find((t) => t.value === e?.eventType),
			fraction: e?.fraction ?? 1,
			minute: e ? String(e.minute) : "",
			recoveryMinutes: String(e?.recoveryMinutes ?? 0),
			sequence: String(
				e?.sequence ||
					Math.max(
						0,
						...(initialData?.events ?? []).map((e) => e.sequence ?? 0),
					) + 1,
			),
			notes: e?.notes ?? "",
		});
		setError("");
	}, [open, initialData, options]);
	const period = initialData?.sportingPeriods?.find(
		(p) => p.number === Number(formData.fraction),
	);
	const position = {
		fraction: Number(formData.fraction),
		minute: Number(formData.minute),
		recoveryMinutes: Number(formData.recoveryMinutes),
		sequence: Number(formData.sequence),
		id: initialData?.event?.id ?? "~",
	};
	const {onPitch, appeared, dismissed, injured, injuryVacancies} =
		participationBeforeEvent(
			players,
			initialData?.events ?? [],
			initialData?.matchSide,
			position,
			initialData?.event?.id,
		);
	const canParticipate = (p) =>
		!dismissed.has(p.value) &&
		!injured.has(p.value) &&
		(!initialData?.participationComplete || onPitch.has(p.value));
	const canEnter = (p) =>
		!dismissed.has(p.value) &&
		!onPitch.has(p.value) &&
		(initialData?.allowReentry || !appeared.has(p.value));
	const canExit = (p) =>
		onPitch.has(p.value) ||
		injuryVacancies.has(p.value) ||
		(initialData?.allowUnknownScorer &&
			p.provisional &&
			!appeared.has(p.value));
	const set = (key, val) => {
		setError("");
		setFormData((f) => ({...f, [key]: val}));
	};
	const save = () => {
		if (!period) {
			setError(
				"Configura i tempi della partita prima di inserire o correggere un evento.",
			);
			return;
		}
		const minute = Number(formData.minute),
			recovery = Number(formData.recoveryMinutes),
			sequence = Number(formData.sequence);
		if (
			formData.minute === "" ||
			!Number.isInteger(minute) ||
			minute < 0 ||
			minute > period.durationMinutes
		) {
			setError(
				`Il minuto del ${period.number}T deve essere tra 0 e ${period.durationMinutes}. Usa il campo Recupero per i minuti aggiuntivi.`,
			);
			return;
		}
		if (
			!Number.isInteger(recovery) ||
			recovery < 0 ||
			(recovery > 0 && minute !== period.durationMinutes)
		) {
			setError(
				`Indica un recupero intero non negativo, soltanto al minuto ${period.durationMinutes}, fine del tempo selezionato.`,
			);
			return;
		}
		if (!Number.isInteger(sequence) || sequence < 1) {
			setError("L'ordine deve essere un intero positivo.");
			return;
		}
		if (
			formData.opponentAssistNumber !== "" &&
			formData.opponentAssistNumber != null &&
			(!Number.isInteger(Number(formData.opponentAssistNumber)) ||
				Number(formData.opponentAssistNumber) < 0 ||
				Number(formData.opponentAssistNumber) > 999)
		) {
			setError("Indica un numero di maglia per l'assist intero da 0 a 999.");
			return;
		}
		if (
			formData.opponentAssistName?.trim() &&
			!formData.assistPlayer &&
			!String(formData.opponentAssistNumber ?? "").trim()
		) {
			setError("Indica il numero di maglia dell'autore dell'assist.");
			return;
		}
		if (
			formData.opponentNumber !== "" &&
			formData.opponentNumber != null &&
			(!Number.isInteger(Number(formData.opponentNumber)) ||
				Number(formData.opponentNumber) < 0 ||
				Number(formData.opponentNumber) > 999)
		) {
			setError("Indica un numero di maglia intero da 0 a 999.");
			return;
		}
		if (
			formData.opponentName?.trim() &&
			!formData.player &&
			(formData.opponentNumber === "" || formData.opponentNumber == null)
		) {
			setError("Indica il numero di maglia del nuovo marcatore.");
			return;
		}
		if (
			(!formData.player && !initialData?.allowUnknownScorer) ||
			(initialData?.type === "sanction" && !formData.eventType)
		) {
			setError("Completa il giocatore e la tipologia dell'evento.");
			return;
		}
		if (
			initialData?.type === "injury_exit" &&
			!formData.player &&
			(formData.opponentNumber === "" || formData.opponentNumber == null)
		) {
			setError(
				"Indica il numero di maglia del giocatore che esce per infortunio.",
			);
			return;
		}
		if (
			initialData?.type === "injury_exit" &&
			formData.player &&
			injured.has(formData.player.value)
		) {
			setError("Il giocatore e gia uscito per infortunio.");
			return;
		}
		if (
			initialData?.type === "injury_exit" &&
			(!initialData?.allowUnknownScorer ||
				(formData.player && !formData.player.provisional)) &&
			(!formData.player || !onPitch.has(formData.player.value))
		) {
			setError("Seleziona il giocatore in campo che esce per infortunio.");
			return;
		}
		if (
			initialData?.type === "substitution" &&
			((!formData.player && !String(formData.opponentNumber ?? "").trim()) ||
				(!formData.substitutePlayer &&
					!String(formData.opponentOutgoingNumber ?? "").trim()) ||
				(formData.player && !canEnter(formData.player)) ||
				(formData.substitutePlayer && !canExit(formData.substitutePlayer)))
		) {
			setError(
				"Chi entra deve essere fuori campo e chi esce in campo al momento indicato. Controlla anche la regola dei rientri.",
			);
			return;
		}
		if (
			(formData.player && dismissed.has(formData.player.value)) ||
			(formData.assistPlayer && dismissed.has(formData.assistPlayer.value))
		) {
			setError(
				"Un giocatore espulso non può partecipare a goal, assist o cambi successivi.",
			);
			return;
		}
		if (
			initialData?.type === "goal" &&
			((formData.player && !canParticipate(formData.player)) ||
				(formData.assistPlayer &&
					(!canParticipate(formData.assistPlayer) ||
						formData.assistPlayer.value === formData.player?.value)))
		) {
			setError(
				"Marcatore e autore dell'assist devono essere giocatori diversi e disponibili al momento del goal.",
			);
			return;
		}
		confirm({
			...formData,
			eventType:
				initialData?.type === "goal" ? formData.goalType : formData.eventType,
			minute,
			fraction: Number(formData.fraction),
			recoveryMinutes: recovery,
			sequence,
		});
	};
	return (
		<VLFModal
			open={open}
			title={initialData?.event ? "Modifica evento" : "Aggiungi evento"}
			close={close}
			confirm={save}
			content={
				<div className="he-wrap">
					{error && (
						<div role="alert" className="text-danger">
							{error}
						</div>
					)}
					{initialData?.event &&
						initialData.event.timeConvention !== "period" && (
							<p>
								Minuto precedente da verificare: salvando confermi che il minuto
								indicato appartiene al tempo selezionato.
							</p>
						)}
					<div className="he-field">
						<span className="he-field__label">Tempo *</span>
						<div className="he-fraction-btns">
							{(initialData?.sportingPeriods ?? []).map((p) => (
								<button
									type="button"
									key={p.number}
									className={`he-fraction-btn${Number(formData.fraction) === p.number ? " he-fraction-btn--active" : ""}`}
									onClick={() => set("fraction", p.number)}
								>
									{p.number}T{p.isExtraTime ? " suppl." : ""} ·{" "}
									{p.durationMinutes}′
								</button>
							))}
						</div>
					</div>
					<div className="he-time-row">
						<div className="he-time-row__minute">
							<VLFTextField
								name="minute"
								value={formData.minute ?? ""}
								onChange={(e) => set("minute", e.target.value)}
								label="Minuto del tempo"
								type="number"
								size="small"
								required
								inputProps={{min: 0, max: period?.durationMinutes, step: 1}}
							/>
						</div>
						<div className="he-time-row__minute">
							<VLFTextField
								name="recoveryMinutes"
								value={formData.recoveryMinutes ?? "0"}
								onChange={(e) => set("recoveryMinutes", e.target.value)}
								label="Recupero"
								type="number"
								size="small"
								inputProps={{min: 0, step: 1}}
							/>
						</div>
					</div>
					{initialData?.type === "goal" && (
						<VLFSelectNew
							label="Evento"
							options={SCORING_TYPES}
							value={formData.goalType}
							required
							onChange={(v) => {
								set("goalType", v);
								set("assistPlayer", null);
								set("opponentAssistNumber", "");
								set("opponentAssistName", "");
							}}
						/>
					)}
					<VLFSelectNew
						options={options.filter((p) =>
							initialData?.type === "substitution"
								? (!p.provisional || initialData?.allowUnknownScorer) &&
									canEnter(p)
								: initialData?.type === "goal"
									? canParticipate(p)
									: (!p.provisional ||
											(initialData?.type === "injury_exit" &&
												initialData?.allowUnknownScorer)) &&
										!dismissed.has(p.value) &&
										!injured.has(p.value) &&
										(initialData?.type !== "injury_exit" ||
											p.provisional ||
											onPitch.has(p.value)),
						)}
						value={formData.player}
						onChange={(v) => {
							set("player", v);
							set("opponentNumber", "");
							set("opponentName", "");
						}}
						required={!initialData?.allowUnknownScorer}
						label={initialData?.type === "substitution" ? "Entra" : "Giocatore"}
						placeholder={
							initialData?.allowUnknownScorer
								? "Giocatore sconosciuto"
								: "Seleziona..."
						}
					/>
					{initialData?.type === "goal" &&
						formData.goalType?.value === "goal" && (
							<VLFSelectNew
								options={options.filter(
									(p) =>
										(initialData?.allowUnknownScorer || !p.provisional) &&
										canParticipate(p) &&
										p.value !== formData.player?.value,
								)}
								value={formData.assistPlayer}
								onChange={(v) => {
									set("assistPlayer", v);
									set("opponentAssistNumber", "");
									set("opponentAssistName", "");
								}}
								label="Assist di (facoltativo)"
							/>
						)}
					{initialData?.type === "goal" &&
						formData.goalType?.value === "goal" &&
						initialData?.allowUnknownScorer &&
						(!formData.assistPlayer || formData.assistPlayer.provisional) && (
							<div className="row">
								<div className="col-4">
									<VLFTextField
										name="opponentAssistNumber"
										label="Numero assist"
										type="number"
										size="small"
										value={
											formData.assistPlayer?.provisional
												? formData.assistPlayer.playerNumber
												: formData.opponentAssistNumber ?? ""
										}
										readOnly={Boolean(formData.assistPlayer?.provisional)}
										inputProps={{min: 0, max: 999, step: 1}}
										onChange={(e) =>
											set("opponentAssistNumber", e.target.value)
										}
									/>
								</div>
								<div className="col-8">
									<VLFTextField
										name="opponentAssistName"
										label="Nome autore assist (facoltativo)"
										size="small"
										value={formData.opponentAssistName ?? ""}
										inputProps={{maxLength: 100}}
										onChange={(e) => set("opponentAssistName", e.target.value)}
									/>
								</div>
							</div>
						)}
					{initialData?.allowUnknownScorer &&
						(!formData.player || formData.player.provisional) && (
							<div className="row">
								<div className="col-4">
									<VLFTextField
										name="opponentNumber"
										label={
											initialData?.type === "substitution"
												? "Numero entrante"
												: "Numero giocatore"
										}
										type="number"
										size="small"
										value={
											formData.player?.provisional
												? formData.player.playerNumber
												: formData.opponentNumber ?? ""
										}
										readOnly={Boolean(formData.player?.provisional)}
										inputProps={{min: 0, max: 999, step: 1}}
										onChange={(e) => set("opponentNumber", e.target.value)}
									/>
								</div>
								<div className="col-8">
									<VLFTextField
										name="opponentName"
										label={
											initialData?.type === "substitution"
												? "Nome entrante (facoltativo)"
												: "Nome giocatore (facoltativo)"
										}
										size="small"
										value={formData.opponentName ?? ""}
										inputProps={{maxLength: 100}}
										onChange={(e) => set("opponentName", e.target.value)}
									/>
								</div>
								<p className="dt-player-meta">
									Il numero aggiunge o riusa il giocatore nella distinta
									parziale, senza creare un'anagrafica. Presenze e minuti
									restano da verificare.
								</p>
							</div>
						)}
					{initialData?.allowUnknownScorer && initialData?.type === "goal" && (
						<span className="dt-player-meta">
							Puoi lasciare il giocatore avversario sconosciuto.
						</span>
					)}
					{initialData?.type === "substitution" && (
						<VLFSelectNew
							options={options.filter(
								(p) =>
									(!p.provisional || initialData?.allowUnknownScorer) &&
									canExit(p),
							)}
							value={formData.substitutePlayer}
							onChange={(v) => {
								set("substitutePlayer", v);
								set("opponentOutgoingNumber", "");
								set("opponentOutgoingName", "");
							}}
							required
							label="Esce"
						/>
					)}
					{initialData?.type === "substitution" &&
						initialData?.allowUnknownScorer &&
						(!formData.substitutePlayer ||
							formData.substitutePlayer.provisional) && (
							<div className="row">
								<div className="col-4">
									<VLFTextField
										name="opponentOutgoingNumber"
										label="Numero uscente"
										type="number"
										size="small"
										value={
											formData.substitutePlayer?.provisional
												? formData.substitutePlayer.playerNumber
												: formData.opponentOutgoingNumber ?? ""
										}
										readOnly={Boolean(formData.substitutePlayer?.provisional)}
										inputProps={{min: 0, max: 999, step: 1}}
										onChange={(e) =>
											set("opponentOutgoingNumber", e.target.value)
										}
									/>
								</div>
								<div className="col-8">
									<VLFTextField
										name="opponentOutgoingName"
										label="Nome uscente (facoltativo)"
										size="small"
										value={formData.opponentOutgoingName ?? ""}
										inputProps={{maxLength: 100}}
										onChange={(e) =>
											set("opponentOutgoingName", e.target.value)
										}
									/>
								</div>
							</div>
						)}
					{initialData?.type === "substitution" && (
						<VLFSelectNew
							label="Motivo del cambio"
							isClearable={false}
							options={[
								{value: "normal", label: "Cambio ordinario"},
								{value: "injury", label: "Infortunio"},
							]}
							value={{
								value: formData.substitutionReason,
								label:
									formData.substitutionReason === "injury"
										? "Infortunio"
										: "Cambio ordinario",
							}}
							onChange={(v) => set("substitutionReason", v.value)}
						/>
					)}
					{initialData?.type === "sanction" && (
						<VLFSelectNew
							options={SANCTION_TYPES}
							value={formData.eventType}
							onChange={(v) => set("eventType", v)}
							required
							label="Tipologia"
							sorted={false}
						/>
					)}
					<VLFTextField
						name="sequence"
						value={formData.sequence ?? ""}
						onChange={(e) => set("sequence", e.target.value)}
						label="Ordine nello stesso minuto"
						type="number"
						size="small"
						inputProps={{min: 1, step: 1}}
						helperText="A parità di tempo, minuto e recupero, viene prima l'ordine più basso."
					/>
					<VLFTextField
						name="notes"
						value={formData.notes ?? ""}
						onChange={(e) => set("notes", e.target.value)}
						label="Note"
						size="small"
						inputProps={{maxLength: 200}}
					/>
				</div>
			}
		/>
	);
}
export default HandleEventModal;
